/***
 *
 *   SCREENING
 *   Manual PEP/Sanctions and Adverse Media screening (feature 011): staff type
 *   a name — plus, for PEP/Sanctions, an optional date of birth — and the
 *   server screens it straight away, with no biometric check and nothing sent
 *   to the client. Each check type owns its form layout (screening-types.js).
 *
 *   Offered only to accounts StackGo ops have enabled, and only the checks the
 *   account is entitled to; the server re-checks both on every request.
 *
 **********/

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import {
  Animate,
  Button,
  Card,
  Form,
  Loader,
  Message,
  Row,
  ViewContext,
} from "components/lib";
import { offeredScreeningTypes, buildScreeningForm } from "./screening-types";

export function ScreeningView() {
  const viewContext = useContext(ViewContext);

  // Loaded here rather than with useAPI, which leaves `loading` set when the
  // request fails and would keep this page on its loader indefinitely.
  const [me, setMe] = useState({ loading: true, data: null, failed: false });
  useEffect(() => {
    let live = true;
    Axios.get("/api/direct-verification/me")
      .then((res) => {
        if (live) setMe({ loading: false, data: res.data.data, failed: false });
      })
      .catch((err) => {
        if (!live) return;
        setMe({ loading: false, data: null, failed: true });
        viewContext.handleError(err);
      });
    return () => {
      live = false;
    };
    // load once per visit; viewContext is stable for the view's lifetime
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const offered = useMemo(
    () => offeredScreeningTypes(me.data?.manual_screening),
    [me.data]
  );

  const [selectedId, setSelectedId] = useState(null);
  const type = offered.find((t) => t.id === selectedId) || offered[0];

  // Bumped to remount the form: "Clear date of birth" resets the DOB to ""
  // in one click while keeping the typed names.
  const [formKey, setFormKey] = useState(0);
  const clearDob = useRef(false);

  // Form writes typed values into the objects it is given, so the previous
  // layout's objects carry what the operator has typed so far.
  const lastForm = useRef(null);

  const formData = useMemo(() => {
    if (!type) return null;
    const previous = lastForm.current;
    const seed = {
      firstName: previous?.firstName?.value || "",
      lastName: previous?.lastName?.value || "",
      dob: clearDob.current ? "" : previous?.dob?.value || "",
    };
    clearDob.current = false;
    const next = buildScreeningForm(type, seed);
    lastForm.current = next;
    return next;
    // formKey is the remount trigger for "Clear date of birth"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, formKey]);

  if (me.loading) return <Loader />;

  if (me.failed) {
    return (
      <Animate>
        <Message
          type="error"
          title="Screening couldn't load"
          text="We couldn't check whether manual screening is enabled for this account. Please reload the page."
        />
      </Animate>
    );
  }

  if (!offered.length) {
    return (
      <Animate>
        <Message
          type="info"
          title="Manual screening isn't enabled"
          text="Manual PEP/Sanctions and Adverse Media screening is available to selected accounts. Contact StackGo support to ask about enabling it."
        />
      </Animate>
    );
  }

  return (
    <Animate>
      <Row>
        <Card title="Screen a name">
          <p className="mb-4">
            Runs a check on the details you enter, with no biometric check and
            nothing sent to the client. The details are recorded as provided.
          </p>

          {offered.length > 1 && (
            <div className="mb-6 flex flex-wrap gap-2">
              {offered.map((option) => (
                <Button
                  key={option.id}
                  small
                  text={option.label}
                  outline={option.id !== type.id}
                  action={() => setSelectedId(option.id)}
                />
              ))}
            </div>
          )}

          <p className="mb-4 font-semibold text-slate-900">{type.label}</p>
          <p className="mb-4">{type.description}</p>

          <Form
            key={`${type.id}-${formKey}`}
            data={formData}
            method="POST"
            url="/api/direct-verification/screen"
            buttonText="Run screen"
            callback={(res) => {
              window.location.href = res.data.data.landingPath;
            }}
          />

          {type.hasDob && (
            <Button
              small
              textOnly
              text="Clear date of birth"
              className="mt-4"
              action={() => {
                clearDob.current = true;
                setFormKey((key) => key + 1);
              }}
            />
          )}
        </Card>
      </Row>
    </Animate>
  );
}
